import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './RegisterPage.css';
import AppIcon from '../components/icons/AppIcon';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://api.halo.ia.br';

// Setores disponíveis
const SECTORS = [
  { value: '', label: 'Selecione seu setor de atuação' },
  { value: 'alimentacao', label: '🍕 Alimentação' },
  { value: 'imoveis', label: '🏠 Imóveis' },
  { value: 'tecnologia', label: '💻 Tecnologia' },
  { value: 'servicos', label: '🔧 Serviços' },
  { value: 'outros', label: '📦 Outros' }
];

// Subsegmentos de Serviços
const SERVICE_TYPES = [
  { value: '', label: 'Selecione o tipo de serviço' },
  { value: 'distribuidora_gas_agua', label: '🔥 Distribuidora de Gás e Água' }
];

// Planos disponíveis
const PLANS = [
  { slug: 'start', name: 'Start', credits: 5000, price: 399, maxAttendance: 60 },
  { slug: 'pro', name: 'Pro', credits: 15000, price: 699, maxAttendance: 200 },
  { slug: 'expert', name: 'Expert', credits: 25000, price: 1099, maxAttendance: 999 }
];

// Opções de volume de atendimentos
const ATTENDANCE_OPTIONS = [
  { value: '', label: 'Selecione a quantidade' },
  { value: '20', label: 'Até 20 atendimentos/dia' },
  { value: '40', label: '20 a 40 atendimentos/dia' },
  { value: '60', label: '40 a 60 atendimentos/dia' },
  { value: '100', label: '60 a 100 atendimentos/dia' },
  { value: '150', label: '100 a 150 atendimentos/dia' },
  { value: '200', label: '150 a 200 atendimentos/dia' },
  { value: '300', label: 'Mais de 200 atendimentos/dia' }
];

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    // Setor
    sector: '',
    serviceType: '',
    
    // Usuário
    userName: '',
    userEmail: '',
    userPhone: '',
    password: '',
    confirmPassword: '',
    
    // Empresa
    companyName: '',
    cnpj: '',
    description: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    website: '',
    instagram: '',
    facebook: '',
    
    // Plano
    attendance: '',
    selectedPlan: 'start',
    
    // Projeto (Distribuidora)
    projectName: '',
    workingHours: {
      segunda: '08:00-18:00',
      terca: '08:00-18:00',
      quarta: '08:00-18:00',
      quinta: '08:00-18:00',
      sexta: '08:00-18:00',
      sabado: '08:00-12:00',
      domingo: ''
    },
    paymentMethods: ['dinheiro', 'pix'],
    deliveryPolicy: '',
    cancellationPolicy: ''
  });
  
  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados de verificação
  const [accessCode, setAccessCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [dataSaved, setDataSaved] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  
  // Estados para novas etapas (após verificação)
  const [currentStep, setCurrentStep] = useState(0); // 0=projeto, 1=whatsapp, 2=produtos, 3=config, 4=revisão
  
  // Conexão WhatsApp
  const [connectionId, setConnectionId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('PENDING');
  const [profileName, setProfileName] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  // Produtos
  const [produtos, setProdutos] = useState([]);
  const [useDefaultProducts, setUseDefaultProducts] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  
  // Configurações
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({
    segunda: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    terca: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    quarta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    quinta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    sexta: { ativo: true, abertura: '08:00', fechamento: '18:00' },
    sabado: { ativo: true, abertura: '08:00', fechamento: '12:00' },
    domingo: { ativo: false, abertura: '', fechamento: '' }
  });
  const [formasPagamento, setFormasPagamento] = useState({
    dinheiro: true,
    pix: true,
    cartao: false
  });
  const [areasCobertura, setAreasCobertura] = useState([]);
  const [tempoMedioEntrega, setTempoMedioEntrega] = useState(45);
  const [chavePix, setChavePix] = useState('');
  
  // Focar no formulário ao montar
  useEffect(() => {
    const container = document.querySelector('.register-form-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  // Buscar CEP
  useEffect(() => {
    if (formData.cep && formData.cep.length === 8) {
      fetch(`https://viacep.com.br/ws/${formData.cep}/json/`)
        .then(res => res.json())
        .then(data => {
          if (!data.erro) {
            setFormData(prev => ({
              ...prev,
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || ''
            }));
          }
        })
        .catch(() => {});
    }
  }, [formData.cep]);
  
  // Sugerir plano baseado em atendimentos
  useEffect(() => {
    const attendance = parseInt(formData.attendance) || 0;
    let suggestedPlan = 'start';
    
    if (attendance <= 60) {
      suggestedPlan = 'start';
    } else if (attendance <= 200) {
      suggestedPlan = 'pro';
    } else {
      suggestedPlan = 'expert';
    }
    
    setFormData(prev => ({ ...prev, selectedPlan: suggestedPlan }));
  }, [formData.attendance]);
  
  // Salvar progresso automaticamente
  useEffect(() => {
    const saveProgress = async () => {
      if (formData.userEmail && !verified) {
        try {
          await fetch(`${BACKEND_URL}/api/register/save-progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: formData.userEmail,
              registrationData: formData
            })
          });
        } catch (e) {
          console.log('Progresso salvo localmente');
        }
      }
    };
    
    const timer = setTimeout(saveProgress, 2000);
    return () => clearTimeout(timer);
  }, [formData, verified]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        paymentMethods: checked 
          ? [...prev.paymentMethods, value]
          : prev.paymentMethods.filter(m => m !== value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  // Validações básicas (usadas em múltiplas funções)
  const validateBasicFields = () => {
    if (!formData.userEmail) {
      setError('Digite seu e-mail');
      return false;
    }
    
    if (!formData.password || formData.password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    
    if (!formData.userName || !formData.companyName) {
      setError('Preencha seu nome e nome da empresa');
      return false;
    }
    
    return true;
  };

  // Salvar dados e solicitar código de acesso (apenas salva no pending)
  const handleSaveData = async () => {
    setError('');
    
    if (!validateBasicFields()) return;
    
    setSaving(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/register/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.userEmail,
          registrationData: formData
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setDataSaved(true);
        setSuccess('Seus dados foram salvos! Aguarde o código de acesso para liberar a criação da empresa.');
      } else {
        setError(result.error || 'Erro ao salvar dados');
      }
    } catch (e) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };
  
  // Clicou em "Já tenho o código" - salva dados (se não existir) e mostra campo de código
  const handleShowCodeInput = async () => {
    setError('');
    
    if (!validateBasicFields()) return;
    
    setSaving(true);
    
    try {
      // Sempre salva/atualiza os dados no pending
      const response = await fetch(`${BACKEND_URL}/api/register/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.userEmail,
          registrationData: formData
        })
      });
      
      const result = await response.json();
      
      // Independente de sucesso ou erro, mostra o campo de código
      setShowCodeInput(true);
      setDataSaved(true);
      
    } catch (e) {
      // Mesmo com erro, mostra o campo de código
      setShowCodeInput(true);
      setDataSaved(true);
    } finally {
      setSaving(false);
    }
  };
  
  // Enviar código de acesso - valida código e CRIA EMPRESA
  const handleVerifyAccessCode = async () => {
    setError('');
    
    if (!accessCode) {
      setError('Digite o código de acesso');
      return;
    }
    
    setVerifying(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/register/verify-access-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.userEmail,
          accessCode: accessCode
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setVerified(true);
        setSuccess('Empresa criada com sucesso! Configure seu projeto abaixo.');
      } else {
        setError(result.error || 'Código inválido');
      }
    } catch (e) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setVerifying(false);
    }
  };
  
  // Verificar se é distribuidora
  const isDistributor = formData.sector === 'servicos' && formData.serviceType === 'distribuidora_gas_agua';
  
  // Verificar se é outro setor (não distribuidora)
  const isOtherSector = formData.sector && formData.sector !== 'servicos';
  
  // Produtos padrão para distribuidora
  const DEFAULT_PRODUCTS = [
    { nome: 'Gás 13kg', descricao: 'Botijão de gás 13kg', preco_venda: 110.00, unidade: 'botijão', destaque: true },
    { nome: 'Gás 20kg', descricao: 'Botijão de gás 20kg', preco_venda: 170.00, unidade: 'botijão', destaque: false },
    { nome: 'Gás 45kg', descricao: 'Botijão de gás 45kg', preco_venda: 380.00, unidade: 'botijão', destaque: false },
    { nome: 'Gás 90kg', descricao: 'Botijão de gás 90kg', preco_venda: 720.00, unidade: 'botijão', destaque: false },
    { nome: 'Água 20L', descricao: 'Garrafão de água 20 litros', preco_venda: 8.00, unidade: 'garrafão', destaque: true },
    { nome: 'Água 500ml', descricao: 'Garrafa de água 500ml', preco_venda: 2.50, unidade: 'unidade', destaque: false },
    { nome: 'Água 1.5L', descricao: 'Garrafa de água 1.5 litros', preco_venda: 4.00, unidade: 'unidade', destaque: false }
  ];
  
  // Carregar produtos padrão ao iniciar
  useEffect(() => {
    if (useDefaultProducts && produtos.length === 0) {
      setProdutos(DEFAULT_PRODUCTS.map((p, i) => ({ ...p, ordem_exibicao: i, ativo: true })));
    }
  }, [useDefaultProducts]);
  
  // Criar conexão WhatsApp
  const handleCreateConnection = async () => {
    setCreatingConnection(true);
    setError('');
    
    try {
      // Enviar email para backend buscar companyId
      const response = await fetch(`${BACKEND_URL}/api/register/create-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.userEmail,
          connectionName: formData.projectName || formData.companyName
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setConnectionId(result.connectionId);
        setQrCode(result.qrCode);
        setConnectionStatus('PENDING');
      } else {
        setError(result.error || 'Erro ao criar conexão');
      }
    } catch (e) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setCreatingConnection(false);
    }
  };
  
  // Verificar status da conexão (polling)
  useEffect(() => {
    if (!connectionId || connectionStatus === 'CONECTADO') return;
    
    const checkStatus = async () => {
      setCheckingStatus(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/register/check-connection-status?connection_id=${connectionId}`);
        const result = await response.json();
        
        if (result.success) {
          setConnectionStatus(result.status);
          setProfileName(result.profileName || '');
          setProfilePicUrl(result.profilePicUrl || '');
          
          if (result.qrCode && !qrCode) {
            setQrCode(result.qrCode);
          }
        }
      } catch (e) {
        console.log('Erro ao verificar status');
      } finally {
        setCheckingStatus(false);
      }
    };
    
    // Verificar imediatamente
    checkStatus();
    
    // Polling a cada 3 segundos
    const interval = setInterval(checkStatus, 3000);
    
    return () => clearInterval(interval);
  }, [connectionId, connectionStatus]);
  
  // Adicionar produto
  const handleAddProduct = (product) => {
    setProdutos(prev => [...prev, { ...product, ordem_exibicao: prev.length }]);
    setShowProductForm(false);
  };
  
  // Remover produto
  const handleRemoveProduct = (index) => {
    setProdutos(prev => prev.filter((_, i) => i !== index));
  };
  
  // Atualizar preço do produto
  const handleUpdateProductPrice = (index, preco) => {
    setProdutos(prev => prev.map((p, i) => i === index ? { ...p, preco_venda: parseFloat(preco) || 0 } : p));
  };
  
  // Adicionar área de cobertura
  const handleAddArea = () => {
    setAreasCobertura(prev => [...prev, { bairro: '', taxa: 0, tempoEntrega: 45 }]);
  };
  
  // Remover área de cobertura
  const handleRemoveArea = (index) => {
    setAreasCobertura(prev => prev.filter((_, i) => i !== index));
  };
  
  // Atualizar área de cobertura
  const handleUpdateArea = (index, field, value) => {
    setAreasCobertura(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };
  
  // Atualizar horário
  const handleUpdateHorario = (dia, field, value) => {
    setHorariosFuncionamento(prev => ({
      ...prev,
      [dia]: { ...prev[dia], [field]: value }
    }));
  };
  
  // Finalizar cadastro completo
  const handleCompleteRegistration = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Montar dados estruturados
      const enderecoBaseEstruturado = {
        logradouro: formData.logradouro,
        numero: formData.numero,
        complemento: formData.complemento,
        bairro: formData.bairro,
        cidade: formData.cidade,
        estado: formData.estado,
        cep: formData.cep
      };
      
      const formasPagamentoEstruturado = Object.entries(formasPagamento)
        .filter(([_, ativo]) => ativo)
        .map(([tipo]) => ({
          tipo,
          ...(tipo === 'pix' && chavePix ? { chave: chavePix } : {})
        }));
      
      const response = await fetch(`${BACKEND_URL}/api/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.userEmail,
          projectData: {
            projectName: formData.projectName || formData.companyName,
            projectDescription: formData.description,
            serviceType: formData.serviceType,
            sector: formData.sector,
            connectionId,
            produtos,
            horariosFuncionamento,
            formasPagamentoEstruturado,
            areasCobertura,
            enderecoBaseEstruturado,
            politicaEntrega: formData.deliveryPolicy,
            politicaCancelamento: formData.cancellationPolicy,
            tempoMedioEntrega,
            // Dados da empresa para atualização
            companyName: formData.companyName,
            description: formData.description,
            userPhone: formData.userPhone,
            userEmail: formData.userEmail,
            logradouro: formData.logradouro,
            numero: formData.numero,
            complemento: formData.complemento,
            bairro: formData.bairro,
            cidade: formData.cidade,
            estado: formData.estado,
            cep: formData.cep,
            website: formData.website,
            instagram: formData.instagram,
            facebook: formData.facebook
          }
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSuccess('Cadastro realizado com sucesso! Redirecionando...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(result.error || 'Erro ao finalizar cadastro');
      }
    } catch (e) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="register-page-container">
      {/* Formulário ocupa a página toda */}
      <div className="register-full">
        <div className="register-form-container">
          {/* Header com logo */}
          <div className="register-header">
            <img 
              src="/assets/images/logo-halo-fundo-trasparente.png" 
              alt="Halo IA" 
              className="register-header-logo"
            />
            <h2 className="register-title">Criar Nova Conta</h2>
            <p className="register-subtitle">Preencha os dados abaixo para começar</p>
          </div>
          
          {error && (
            <div className="register-error">
              <AppIcon name="error" />
              {error}
            </div>
          )}
          
          {success && (
            <div className="register-success">
              <AppIcon name="check_circle" />
              {success}
            </div>
          )}
          
          <form className="register-form" onSubmit={(e) => e.preventDefault()}>
            
            {/* Seção: Setor */}
            <section className="register-section">
              <h3 className="section-title">
                <AppIcon name="business" />
                Setor de Atuação
              </h3>
              
              <div className="form-group">
                <label>Setor *</label>
                <select 
                  name="sector" 
                  value={formData.sector} 
                  onChange={handleChange}
                  className="form-select"
                >
                  {SECTORS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              
              {formData.sector === 'servicos' && (
                <div className="form-group">
                  <label>Tipo de Serviço *</label>
                  <select 
                    name="serviceType" 
                    value={formData.serviceType} 
                    onChange={handleChange}
                    className="form-select"
                  >
                    {SERVICE_TYPES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {isOtherSector && (
                <div className="info-box">
                  <AppIcon name="info" />
                  <p>
                    Para este setor, acesse <a href="https://app.halo.ia.br" target="_blank" rel="noopener">app.halo.ia.br</a> para criar sua conta.
                  </p>
                </div>
              )}
            </section>
            
            {/* Seções seguintes só aparecem para Distribuidora */}
            {isDistributor && (
              <>
                {/* Seção: Dados do Usuário */}
                <section className="register-section">
                  <h3 className="section-title">
                    <AppIcon name="person" />
                    Dados do Administrador
                  </h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nome completo *</label>
                      <input 
                        type="text" 
                        name="userName" 
                        value={formData.userName} 
                        onChange={handleChange}
                        placeholder="Seu nome"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>WhatsApp *</label>
                      <input 
                        type="tel" 
                        name="userPhone" 
                        value={formData.userPhone} 
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>E-mail *</label>
                    <input 
                      type="email" 
                      name="userEmail" 
                      value={formData.userEmail} 
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Senha *</label>
                      <input 
                        type="password" 
                        name="password" 
                        value={formData.password} 
                        onChange={handleChange}
                        placeholder="Mínimo 6 caracteres"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Confirmar senha *</label>
                      <input 
                        type="password" 
                        name="confirmPassword" 
                        value={formData.confirmPassword} 
                        onChange={handleChange}
                        placeholder="Repita a senha"
                        className="form-input"
                      />
                    </div>
                  </div>
                </section>
                
                {/* Seção: Dados da Empresa */}
                <section className="register-section">
                  <h3 className="section-title">
                    <AppIcon name="store" />
                    Dados da Empresa
                  </h3>
                  
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>Nome da Empresa *</label>
                      <input 
                        type="text" 
                        name="companyName" 
                        value={formData.companyName} 
                        onChange={handleChange}
                        placeholder="Nome fantasia"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>CNPJ</label>
                      <input 
                        type="text" 
                        name="cnpj" 
                        value={formData.cnpj} 
                        onChange={handleChange}
                        placeholder="00.000.000/0000-00"
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Descrição da empresa</label>
                    <textarea 
                      name="description" 
                      value={formData.description} 
                      onChange={handleChange}
                      placeholder="Descreva brevemente sua empresa..."
                      className="form-textarea"
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>CEP *</label>
                      <input 
                        type="text" 
                        name="cep" 
                        value={formData.cep} 
                        onChange={handleChange}
                        placeholder="00000000"
                        className="form-input"
                        maxLength={8}
                      />
                    </div>
                    
                    <div className="form-group flex-2">
                      <label>Logradouro *</label>
                      <input 
                        type="text" 
                        name="logradouro" 
                        value={formData.logradouro} 
                        onChange={handleChange}
                        placeholder="Rua, Avenida..."
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Número *</label>
                      <input 
                        type="text" 
                        name="numero" 
                        value={formData.numero} 
                        onChange={handleChange}
                        placeholder="Nº"
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Complemento</label>
                      <input 
                        type="text" 
                        name="complemento" 
                        value={formData.complemento} 
                        onChange={handleChange}
                        placeholder="Sala, Andar..."
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Bairro *</label>
                      <input 
                        type="text" 
                        name="bairro" 
                        value={formData.bairro} 
                        onChange={handleChange}
                        placeholder="Bairro"
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>Cidade *</label>
                      <input 
                        type="text" 
                        name="cidade" 
                        value={formData.cidade} 
                        onChange={handleChange}
                        placeholder="Cidade"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Estado *</label>
                      <input 
                        type="text" 
                        name="estado" 
                        value={formData.estado} 
                        onChange={handleChange}
                        placeholder="UF"
                        className="form-input"
                        maxLength={2}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Website</label>
                      <input 
                        type="text" 
                        name="website" 
                        value={formData.website} 
                        onChange={handleChange}
                        placeholder="www.suaempresa.com.br"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Instagram</label>
                      <input 
                        type="text" 
                        name="instagram" 
                        value={formData.instagram} 
                        onChange={handleChange}
                        placeholder="@suaempresa"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Facebook</label>
                      <input 
                        type="text" 
                        name="facebook" 
                        value={formData.facebook} 
                        onChange={handleChange}
                        placeholder="facebook.com/suaempresa"
                        className="form-input"
                      />
                    </div>
                  </div>
                </section>
                
                {/* Seção: Volume e Plano */}
                <section className="register-section">
                  <h3 className="section-title">
                    <AppIcon name="workspace_premium" />
                    Volume de Atendimentos e Plano
                  </h3>
                  
                  <div className="form-group">
                    <label>Quantidade de atendimentos diários *</label>
                    <select 
                      name="attendance" 
                      value={formData.attendance} 
                      onChange={handleChange}
                      className="form-select"
                    >
                      {ATTENDANCE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {formData.attendance && (
                    <div className="plan-suggestion">
                      <p className="suggestion-text">
                        Plano sugerido: <strong>{PLANS.find(p => p.slug === formData.selectedPlan)?.name}</strong>
                      </p>
                    </div>
                  )}
                  
                  <div className="plans-grid">
                    {PLANS.map(plan => (
                      <div 
                        key={plan.slug}
                        className={`plan-card ${formData.selectedPlan === plan.slug ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, selectedPlan: plan.slug }))}
                      >
                        <div className="plan-header">
                          <h4>{plan.name}</h4>
                          {formData.selectedPlan === plan.slug && (
                            <AppIcon name="check_circle" className="check-icon" />
                          )}
                        </div>
                        <div className="plan-price">
                          R$ {plan.price}<span>/mês</span>
                        </div>
                        <div className="plan-credits">
                          {plan.credits.toLocaleString('pt-BR')} créditos/mês
                        </div>
                        <div className="plan-limit">
                          Até {plan.maxAttendance === 999 ? '500+' : plan.maxAttendance} atendimentos/dia
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
                
                {/* Seção: Código de Acesso - ANTES dos campos do projeto */}
                {!verified ? (
                  <section className="register-section verification-section">
                    <h3 className="section-title">
                      <AppIcon name="vpn_key" />
                      Código de Acesso
                    </h3>
                    
                    {!dataSaved && !showCodeInput ? (
                      <div className="verification-actions">
                        <p className="verification-text">
                          Preencha os dados acima e escolha uma das opções abaixo:
                        </p>
                        <div className="verification-buttons-row">
                          <button 
                            type="button"
                            className="btn-primary btn-large"
                            onClick={handleSaveData}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <AppIcon name="progress_activity" className="spinning" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <AppIcon name="save" />
                                Solicitar 7 dias grátis
                              </>
                            )}
                          </button>
                          <button 
                            type="button"
                            className="btn-secondary btn-large"
                            onClick={handleShowCodeInput}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <AppIcon name="progress_activity" className="spinning" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <AppIcon name="vpn_key" />
                                Já tenho o código
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : dataSaved && !showCodeInput ? (
                      <div className="verification-actions">
                        <p className="verification-text">
                          Seus dados foram salvos! Quando receber o código de acesso, insira abaixo:
                        </p>
                        <div className="code-input-row">
                          <input 
                            type="text" 
                            value={accessCode}
                            onChange={(e) => setAccessCode(e.target.value.trim())}
                            placeholder="Digite o código de acesso"
                            className="code-input"
                          />
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={handleVerifyAccessCode}
                            disabled={verifying || !accessCode}
                          >
                            {verifying ? (
                              <AppIcon name="progress_activity" className="spinning" />
                            ) : (
                              'Validar'
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="verification-code-input">
                        <p className="verification-text">
                          Digite o código de acesso que você recebeu
                        </p>
                        <div className="code-input-row">
                          <input 
                            type="text" 
                            value={accessCode}
                            onChange={(e) => setAccessCode(e.target.value.trim())}
                            placeholder="Código de acesso"
                            className="code-input"
                          />
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={handleVerifyAccessCode}
                            disabled={verifying || !accessCode}
                          >
                            {verifying ? (
                              <AppIcon name="progress_activity" className="spinning" />
                            ) : (
                              'Validar'
                            )}
                          </button>
                        </div>
                        <button 
                          type="button"
                          className="btn-link"
                          onClick={() => setShowCodeInput(false)}
                        >
                          Voltar
                        </button>
                      </div>
                    )}
                  </section>
                ) : (
                  <>
                    {/* STEPPER - Indicador de progresso */}
                    <section className="register-section stepper-section">
                      <div className="stepper">
                        {['Projeto', 'WhatsApp', 'Produtos', 'Configurações', 'Revisão'].map((step, i) => (
                          <div 
                            key={step}
                            className={`stepper-item ${currentStep >= i ? 'active' : ''} ${currentStep === i ? 'current' : ''}`}
                            onClick={() => currentStep > i && setCurrentStep(i)}
                          >
                            <div className="stepper-number">{i + 1}</div>
                            <div className="stepper-label">{step}</div>
                          </div>
                        ))}
                      </div>
                    </section>
                    
                    {/* ETAPA 0: Nome do Projeto */}
                    {currentStep === 0 && (
                      <section className="register-section">
                        <h3 className="section-title">
                          <AppIcon name="folder" />
                          Nome do Projeto
                        </h3>
                        
                        <div className="form-group">
                          <label>Nome do Projeto *</label>
                          <input 
                            type="text" 
                            name="projectName" 
                            value={formData.projectName} 
                            onChange={handleChange}
                            placeholder={formData.companyName || 'Nome do projeto'}
                            className="form-input"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Descrição do Projeto</label>
                          <textarea 
                            name="description" 
                            value={formData.description} 
                            onChange={handleChange}
                            placeholder="Descreva brevemente seu projeto..."
                            className="form-textarea"
                            rows={3}
                          />
                        </div>
                        
                        <div className="step-actions">
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={() => setCurrentStep(1)}
                            disabled={!formData.projectName && !formData.companyName}
                          >
                            Próximo
                            <AppIcon name="arrow_forward" />
                          </button>
                        </div>
                      </section>
                    )}
                    
                    {/* ETAPA 1: Conexão WhatsApp */}
                    {currentStep === 1 && (
                      <section className="register-section">
                        <h3 className="section-title">
                          <AppIcon name="qr_code" />
                          Conexão WhatsApp
                        </h3>
                        
                        {!connectionId ? (
                          <div className="whatsapp-connect">
                            <p className="info-text">
                              Conecte seu WhatsApp para habilitar o atendimento automático
                            </p>
                            <button 
                              type="button"
                              className="btn-primary btn-large"
                              onClick={handleCreateConnection}
                              disabled={creatingConnection}
                            >
                              {creatingConnection ? (
                                <>
                                  <AppIcon name="progress_activity" className="spinning" />
                                  Criando conexão...
                                </>
                              ) : (
                                <>
                                  <AppIcon name="qr_code" />
                                  Gerar QR Code
                                </>
                              )}
                            </button>
                          </div>
                        ) : connectionStatus === 'CONECTADO' ? (
                          <div className="whatsapp-connected">
                            <div className="connected-header">
                              <AppIcon name="check_circle" className="success-icon" />
                              <h4>WhatsApp Conectado!</h4>
                            </div>
                            {profilePicUrl && (
                              <img src={profilePicUrl} alt="Perfil" className="profile-pic" />
                            )}
                            <p className="profile-name">{profileName || 'Conectado'}</p>
                          </div>
                        ) : (
                          <div className="whatsapp-qrcode">
                            <p className="info-text">
                              Escaneie o QR Code com seu WhatsApp:
                            </p>
                            {qrCode && (
                              <div className="qrcode-container">
                                <img src={qrCode} alt="QR Code WhatsApp" className="qrcode-image" />
                              </div>
                            )}
                            <p className="status-text">
                              {checkingStatus ? (
                                <AppIcon name="progress_activity" className="spinning" />
                              ) : (
                                'Aguardando escaneamento...'
                              )}
                            </p>
                          </div>
                        )}
                        
                        <div className="step-actions">
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={() => setCurrentStep(0)}
                          >
                            <AppIcon name="arrow_back" />
                            Voltar
                          </button>
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={() => setCurrentStep(2)}
                            disabled={connectionStatus !== 'CONECTADO'}
                          >
                            Próximo
                            <AppIcon name="arrow_forward" />
                          </button>
                        </div>
                      </section>
                    )}
                    
                    {/* ETAPA 2: Produtos */}
                    {currentStep === 2 && (
                      <section className="register-section">
                        <h3 className="section-title">
                          <AppIcon name="inventory_2" />
                          Produtos Disponíveis
                        </h3>
                        
                        <div className="form-group">
                          <label className="checkbox-label">
                            <input 
                              type="checkbox"
                              checked={useDefaultProducts}
                              onChange={(e) => {
                                setUseDefaultProducts(e.target.checked);
                                if (e.target.checked) {
                                  setProdutos(DEFAULT_PRODUCTS.map((p, i) => ({ ...p, ordem_exibicao: i, ativo: true })));
                                } else {
                                  setProdutos([]);
                                }
                              }}
                            />
                            Usar produtos padrão para distribuidora
                          </label>
                        </div>
                        
                        {produtos.length > 0 && (
                          <div className="products-list">
                            {produtos.map((produto, index) => (
                              <div key={index} className="product-item">
                                <div className="product-info">
                                  <span className="product-name">{produto.nome}</span>
                                  <span className="product-unit">({produto.unidade})</span>
                                </div>
                                <div className="product-price">
                                  <label>R$</label>
                                  <input 
                                    type="number"
                                    value={produto.preco_venda}
                                    onChange={(e) => handleUpdateProductPrice(index, e.target.value)}
                                    className="price-input"
                                    step="0.01"
                                  />
                                </div>
                                <button 
                                  type="button"
                                  className="btn-icon btn-remove"
                                  onClick={() => handleRemoveProduct(index)}
                                >
                                  <AppIcon name="delete" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <button 
                          type="button"
                          className="btn-secondary"
                          onClick={() => setShowProductForm(true)}
                        >
                          <AppIcon name="add" />
                          Adicionar Produto
                        </button>
                        
                        <div className="step-actions">
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={() => setCurrentStep(1)}
                          >
                            <AppIcon name="arrow_back" />
                            Voltar
                          </button>
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={() => setCurrentStep(3)}
                            disabled={produtos.length === 0}
                          >
                            Próximo
                            <AppIcon name="arrow_forward" />
                          </button>
                        </div>
                      </section>
                    )}
                    
                    {/* ETAPA 3: Configurações */}
                    {currentStep === 3 && (
                      <section className="register-section">
                        <h3 className="section-title">
                          <AppIcon name="settings" />
                          Configurações
                        </h3>
                        
                        {/* Horários de Funcionamento */}
                        <div className="config-section">
                          <h4>Horários de Funcionamento</h4>
                          <div className="horarios-grid">
                            {Object.entries(horariosFuncionamento).map(([dia, config]) => (
                              <div key={dia} className="horario-row">
                                <label className="checkbox-label">
                                  <input 
                                    type="checkbox"
                                    checked={config.ativo}
                                    onChange={(e) => handleUpdateHorario(dia, 'ativo', e.target.checked)}
                                  />
                                  {dia.charAt(0).toUpperCase() + dia.slice(1)}
                                </label>
                                {config.ativo && (
                                  <>
                                    <input 
                                      type="time"
                                      value={config.abertura}
                                      onChange={(e) => handleUpdateHorario(dia, 'abertura', e.target.value)}
                                      className="time-input"
                                    />
                                    <span>às</span>
                                    <input 
                                      type="time"
                                      value={config.fechamento}
                                      onChange={(e) => handleUpdateHorario(dia, 'fechamento', e.target.value)}
                                      className="time-input"
                                    />
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Formas de Pagamento */}
                        <div className="config-section">
                          <h4>Formas de Pagamento</h4>
                          <div className="checkbox-group">
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={formasPagamento.dinheiro}
                                onChange={(e) => setFormasPagamento(prev => ({ ...prev, dinheiro: e.target.checked }))}
                              />
                              💵 Dinheiro
                            </label>
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={formasPagamento.pix}
                                onChange={(e) => setFormasPagamento(prev => ({ ...prev, pix: e.target.checked }))}
                              />
                              📱 PIX
                            </label>
                            <label className="checkbox-label">
                              <input 
                                type="checkbox"
                                checked={formasPagamento.cartao}
                                onChange={(e) => setFormasPagamento(prev => ({ ...prev, cartao: e.target.checked }))}
                              />
                              💳 Cartão
                            </label>
                          </div>
                          {formasPagamento.pix && (
                            <div className="form-group mt-2">
                              <label>Chave PIX</label>
                              <input 
                                type="text"
                                value={chavePix}
                                onChange={(e) => setChavePix(e.target.value)}
                                placeholder="Sua chave PIX"
                                className="form-input"
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Tempo Médio de Entrega */}
                        <div className="config-section">
                          <h4>Tempo Médio de Entrega</h4>
                          <div className="form-group">
                            <input 
                              type="number"
                              value={tempoMedioEntrega}
                              onChange={(e) => setTempoMedioEntrega(parseInt(e.target.value) || 45)}
                              className="form-input"
                              min="1"
                            />
                            <span>minutos</span>
                          </div>
                        </div>
                        
                        {/* Áreas de Cobertura */}
                        <div className="config-section">
                          <h4>Áreas de Cobertura</h4>
                          {areasCobertura.map((area, index) => (
                            <div key={index} className="area-row">
                              <input 
                                type="text"
                                value={area.bairro}
                                onChange={(e) => handleUpdateArea(index, 'bairro', e.target.value)}
                                placeholder="Bairro"
                                className="form-input"
                              />
                              <input 
                                type="number"
                                value={area.taxa}
                                onChange={(e) => handleUpdateArea(index, 'taxa', parseFloat(e.target.value) || 0)}
                                placeholder="Taxa R$"
                                className="form-input small"
                              />
                              <input 
                                type="number"
                                value={area.tempoEntrega}
                                onChange={(e) => handleUpdateArea(index, 'tempoEntrega', parseInt(e.target.value) || 45)}
                                placeholder="Min"
                                className="form-input small"
                              />
                              <button 
                                type="button"
                                className="btn-icon btn-remove"
                                onClick={() => handleRemoveArea(index)}
                              >
                                <AppIcon name="delete" />
                              </button>
                            </div>
                          ))}
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={handleAddArea}
                          >
                            <AppIcon name="add" />
                            Adicionar Área
                          </button>
                        </div>
                        
                        {/* Políticas */}
                        <div className="config-section">
                          <h4>Políticas</h4>
                          <div className="form-group">
                            <label>Política de Entrega</label>
                            <textarea 
                              name="deliveryPolicy" 
                              value={formData.deliveryPolicy} 
                              onChange={handleChange}
                              placeholder="Ex: Entrega em até 45 minutos para pedidos imediatos..."
                              className="form-textarea"
                              rows={2}
                            />
                          </div>
                          <div className="form-group">
                            <label>Política de Cancelamento</label>
                            <textarea 
                              name="cancellationPolicy" 
                              value={formData.cancellationPolicy} 
                              onChange={handleChange}
                              placeholder="Ex: Cancelamento gratuito até 1 hora antes da entrega..."
                              className="form-textarea"
                              rows={2}
                            />
                          </div>
                        </div>
                        
                        <div className="step-actions">
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={() => setCurrentStep(2)}
                          >
                            <AppIcon name="arrow_back" />
                            Voltar
                          </button>
                          <button 
                            type="button"
                            className="btn-primary"
                            onClick={() => setCurrentStep(4)}
                          >
                            Próximo
                            <AppIcon name="arrow_forward" />
                          </button>
                        </div>
                      </section>
                    )}
                    
                    {/* ETAPA 4: Revisão Final */}
                    {currentStep === 4 && (
                      <section className="register-section review-section">
                        <h3 className="section-title">
                          <AppIcon name="fact_check" />
                          Revisão Final
                        </h3>
                        
                        <div className="review-grid">
                          <div className="review-item">
                            <h4>Projeto</h4>
                            <p><strong>Nome:</strong> {formData.projectName || formData.companyName}</p>
                            <p><strong>Descrição:</strong> {formData.description || '-'}</p>
                          </div>
                          
                          <div className="review-item">
                            <h4>WhatsApp</h4>
                            <p><strong>Status:</strong> {connectionStatus === 'CONECTADO' ? '✅ Conectado' : '❌ Não conectado'}</p>
                            {profileName && <p><strong>Perfil:</strong> {profileName}</p>}
                          </div>
                          
                          <div className="review-item">
                            <h4>Produtos ({produtos.length})</h4>
                            {produtos.slice(0, 3).map((p, i) => (
                              <p key={i}>{p.nome} - R$ {p.preco_venda.toFixed(2)}</p>
                            ))}
                            {produtos.length > 3 && <p>...e mais {produtos.length - 3} produtos</p>}
                          </div>
                          
                          <div className="review-item">
                            <h4>Configurações</h4>
                            <p><strong>Tempo entrega:</strong> {tempoMedioEntrega} min</p>
                            <p><strong>Pagamentos:</strong> {Object.entries(formasPagamento).filter(([_, v]) => v).map(([k]) => k).join(', ')}</p>
                            <p><strong>Áreas:</strong> {areasCobertura.length || 'Todas'}</p>
                          </div>
                        </div>
                        
                        <div className="step-actions">
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={() => setCurrentStep(3)}
                          >
                            <AppIcon name="arrow_back" />
                            Voltar
                          </button>
                          <button 
                            type="button"
                            className="btn-primary btn-large"
                            onClick={handleCompleteRegistration}
                            disabled={loading || connectionStatus !== 'CONECTADO'}
                          >
                            {loading ? (
                              <>
                                <AppIcon name="progress_activity" className="spinning" />
                                Finalizando...
                              </>
                            ) : (
                              <>
                                <AppIcon name="check_circle" />
                                Finalizar Cadastro
                              </>
                            )}
                          </button>
                        </div>
                      </section>
                    )}
                  </>
                )}
              </>
            )}
            
          </form>
          
          <div className="register-footer">
            <p>Já tem uma conta? <a href="/login">Fazer login</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
